import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import socket from '../services/socket';
import '../styles/AdminTimeline.css';

// Get admin token for API calls
const getAdminToken = () => localStorage.getItem('adminKey') || '';

// Party timeline phases in order with detailed events
const TIMELINE_PHASES = [
  {
    id: 'not-started',
    name: 'Not Started',
    icon: '⏸️',
    duration: 0,
    description: 'Party has not begun - waiting for host to start',
    nextAction: 'Start Registration + Premarket',
    notes: '👆 Click "Start Party" to begin. This opens registration for guests.',
    events: [],
    actions: [
      { id: 'start-party', label: '🚀 Start Party', endpoint: '/api/admin/show/registration', body: { action: 'start' } }
    ]
  },
  {
    id: 'registration-premarket',
    name: 'Registration + Premarket',
    icon: '📝📺',
    duration: 45,
    description: 'PARALLEL: Guests register WHILE premarket broadcast plays',
    nextAction: 'Start Orientation',
    notes: '⏳ Let guests register. When ready, click "Play Orientation" to start the video. Registration stays open throughout the party.',
    events: [
      { name: 'registration-start', description: 'Open registration', target: 'Web + ESP32' },
      { name: 'premarket-start', description: 'Start broadcast', target: 'Web + ESP32' },
      { name: 'player-registered', description: 'Each player scan', target: 'Web + ESP32' },
      { name: 'orientation-warning', description: '10/5/3/1 min warnings until orientation', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'start-registration', label: '📝 Start Registration', endpoint: '/api/admin/show/registration', body: { action: 'start' } },
      { id: 'start-premarket', label: '📺 Start Premarket', endpoint: '/api/admin/show/premarket', body: { action: 'start', minutes: 30 } },
      { id: 'warning-10', label: '🎬 10min to Orientation', endpoint: '/api/admin/show/warning', body: { type: 'orientation', minutes: 10 } },
      { id: 'warning-5', label: '🎬 5min to Orientation', endpoint: '/api/admin/show/warning', body: { type: 'orientation', minutes: 5 } }
    ]
  },
  {
    id: 'orientation',
    name: 'Orientation Video',
    icon: '🎬',
    duration: 10,
    description: 'Tutorial video + Q&A',
    nextAction: 'Opening Bell Countdown',
    notes: '🎬 Play the orientation video. When done, click "End Orientation" then start the Opening Bell countdown.',
    events: [
      { name: 'orientation-start', description: 'Play orientation video', target: 'Web + ESP32' },
      { name: 'orientation-end', description: 'Video complete', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'orientation', label: '🎬 Play Orientation', endpoint: '/api/admin/show/orientation', body: { action: 'start' } },
      { id: 'end-orientation', label: '✓ End Orientation', endpoint: '/api/admin/show/orientation', body: { action: 'end' } }
    ]
  },
  {
    id: 'opening-countdown',
    name: 'Opening Bell Countdown',
    icon: '⏱️',
    duration: 3,
    description: 'Dramatic countdown to market open',
    nextAction: 'Asian Session',
    notes: '👆 HARD countdown (max 3 min) - auto-opens market AND starts Phase 1 (Asian) with macros! Use "Open Market NOW" to skip.',
    events: [
      { name: 'countdown-start', description: 'Countdown to market open', target: 'Web + ESP32' },
      { name: 'countdown-update', description: 'Countdown modified', target: 'Web + ESP32' },
      { name: 'market-bell (open)', description: 'Market opens', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'countdown-open', label: '⏱️ Opening Countdown (HARD)', endpoint: '/api/admin/show/countdown', body: { seconds: 180, autoOpenMarket: true, countdownType: 'market-open' }, hasCountdownInput: true, defaultSeconds: 180 },
      { id: 'open-now', label: '🔔 Open Market NOW', endpoint: '/api/admin/market/toggle', body: { action: 'open' } }
    ]
  },
  {
    id: 'trading1',
    name: 'Asian Session',
    icon: '🌏',
    duration: 30,
    description: 'First trading session - ~4-5 stock events + 1 BULLISH macro @ 15min',
    nextAction: 'Start Portfolio',
    notes: '📈 Auto-started by Opening Bell! Soft countdown is a guide. When ready for Portfolio game, click "Pause for Portfolio".',
    events: [
      { name: 'trading-phase-started', description: 'Phase 1 started + macros scheduled', target: 'Web + ESP32' },
      { name: 'market-event', description: '~4-5 random stock events (different stocks)', target: 'Web + ESP32' },
      { name: 'market-event (BULLISH)', description: '📈 Auto BULLISH macro @ 15min', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'start-phase-1', label: '🌏 Start Phase 1 (soft)', endpoint: '/api/admin/trading-phase/start', body: { phase: 1 } },
      { id: 'countdown-portfolio', label: '🔔 Portfolio Countdown (HARD)', endpoint: '/api/admin/show/macro-countdown', body: { gameName: 'Portfolio Warfare', seconds: 180 }, hasCountdownInput: true, defaultSeconds: 180, isPrimary: true },
      { id: 'pause-portfolio', label: '⏸️ Pause NOW', endpoint: '/api/admin/trading/pause', body: {} }
    ]
  },
  {
    id: 'battleship',
    name: 'Portfolio',
    icon: '💼',
    duration: 50,
    description: 'Portfolio Warfare - 4 teams battleship',
    nextAction: 'European Session',
    notes: '🎮 Run the game! When finished, click "Start Phase 2 (European)" to begin next trading session with auto-scheduled macros.',
    events: [
      { name: 'macro-game-countdown', description: 'Countdown to game start', target: 'Web + ESP32' },
      { name: 'trading-paused', description: 'Halt all trading', target: 'Web + ESP32' },
      { name: 'big-game-announcement', description: 'Announce game', target: 'Web + ESP32' },
      { name: 'battleship:game-start', description: 'Game begins', target: 'Web + ESP32' },
      { name: 'battleship:turn', description: 'Turn changes', target: 'Web + ESP32' },
      { name: 'battleship:hit/miss', description: 'Attack results', target: 'Web + ESP32' },
      { name: 'battleship:eliminated', description: 'Team out', target: 'Web + ESP32' },
      { name: 'battleship:game-over', description: 'Winner declared', target: 'Web + ESP32' },
      { name: 'trading-phase-started', description: 'Next phase starts', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'announce-portfolio', label: '📢 Announce Game', endpoint: '/api/admin/big-game/announce', body: { gameName: 'Portfolio', announcement: 'Portfolio Warfare is starting!', pauseTrading: true } },
      { id: 'start-phase-2', label: '🇪🇺 Start Phase 2 (European)', endpoint: '/api/admin/trading-phase/start', body: { phase: 2 }, isPrimary: true }
    ]
  },
  {
    id: 'trading2',
    name: 'European Session',
    icon: '🇪🇺',
    duration: 30,
    description: 'Second trading session - ~4-5 stock events + 2 BEARISH macros @ 10min, 25min',
    nextAction: 'Start Dead Cat Bounce',
    notes: '📉 Click "Start Phase 2" to begin trading. Countdown is a guide - when ready for Dead Cat Bounce, click "Pause for Minigolf".',
    events: [
      { name: 'trading-phase-started', description: 'Phase 2 started + macros scheduled', target: 'Web + ESP32' },
      { name: 'trading-resumed', description: '🔔 Resume bell (smaller than opening) + toast', target: 'Web + ESP32' },
      { name: 'market-event', description: '~4-5 random stock events (different stocks)', target: 'Web + ESP32' },
      { name: 'market-event (BEARISH)', description: '📉 Auto BEARISH macro @ 10min', target: 'Web + ESP32' },
      { name: 'market-event (BEARISH)', description: '📉 Auto BEARISH macro @ 25min', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'start-phase-2', label: '🇪🇺 Start Phase 2 (soft)', endpoint: '/api/admin/trading-phase/start', body: { phase: 2 } },
      { id: 'countdown-minigolf', label: '🔔 Dead Cat Countdown (HARD)', endpoint: '/api/admin/show/macro-countdown', body: { gameName: 'Dead Cat Bounce', seconds: 180 }, hasCountdownInput: true, defaultSeconds: 180, isPrimary: true },
      { id: 'pause-minigolf', label: '⏸️ Pause NOW', endpoint: '/api/admin/trading/pause', body: {} }
    ]
  },
  {
    id: 'minigolf',
    name: 'Dead Cat Bounce',
    icon: '⛳',
    duration: 30,
    description: 'Team relay putting with obstacles',
    nextAction: 'Canadian Session',
    notes: '🎮 Run the game! When finished, click "Start Phase 3 (Canadian)" to begin next trading session with auto-scheduled macros.',
    events: [
      { name: 'macro-game-countdown', description: 'Countdown to game start', target: 'Web + ESP32' },
      { name: 'trading-paused', description: 'Halt all trading', target: 'Web + ESP32' },
      { name: 'big-game-announcement', description: 'Announce game', target: 'Web + ESP32' },
      { name: 'trading-phase-started', description: 'Next phase starts', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'announce-minigolf', label: '📢 Announce Game', endpoint: '/api/admin/big-game/announce', body: { gameName: 'Dead Cat Bounce', announcement: 'Dead Cat Bounce is starting!', pauseTrading: true } },
      { id: 'start-phase-3', label: '🇨🇦 Start Phase 3 (Canadian)', endpoint: '/api/admin/trading-phase/start', body: { phase: 3 }, isPrimary: true }
    ]
  },
  {
    id: 'trading3',
    name: 'Canadian Session',
    icon: '🇨🇦',
    duration: 30,
    description: 'Third trading session - ~4-5 stock events + 1 BULLISH macro @ 15min',
    nextAction: 'Start A Brief Case',
    notes: '📈 Click "Start Phase 3" to begin trading. Countdown is a guide - when ready for A Brief Case, click "Pause for Briefcase".',
    events: [
      { name: 'trading-phase-started', description: 'Phase 3 started + macros scheduled', target: 'Web + ESP32' },
      { name: 'trading-resumed', description: '🔔 Resume bell (smaller than opening) + toast', target: 'Web + ESP32' },
      { name: 'market-event', description: '~4-5 random stock events (different stocks)', target: 'Web + ESP32' },
      { name: 'market-event (BULLISH)', description: '📈 Auto BULLISH macro @ 15min', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'start-phase-3', label: '🇨🇦 Start Phase 3 (soft)', endpoint: '/api/admin/trading-phase/start', body: { phase: 3 } },
      { id: 'countdown-briefcase', label: '🔔 Briefcase Countdown (HARD)', endpoint: '/api/admin/show/macro-countdown', body: { gameName: 'A Brief Case', seconds: 180 }, hasCountdownInput: true, defaultSeconds: 180, isPrimary: true },
      { id: 'pause-briefcase', label: '⏸️ Pause NOW', endpoint: '/api/admin/trading/pause', body: {} }
    ]
  },
  {
    id: 'briefcase',
    name: 'A Brief Case',
    icon: '💰',
    duration: 30,
    description: 'Deal or No Deal style game',
    nextAction: 'Power Hour',
    notes: '🎮 Run the game! When finished, click "Start Phase 4 (Power Hour)" - this starts the 5-min pure trading session with NO events.',
    events: [
      { name: 'macro-game-countdown', description: 'Countdown to game start', target: 'Web + ESP32' },
      { name: 'trading-paused', description: 'Halt all trading', target: 'Web + ESP32' },
      { name: 'big-game-announcement', description: 'Announce game', target: 'Web + ESP32' },
      { name: 'trading-phase-started', description: 'Power Hour starts', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'announce-briefcase', label: '📢 Announce Game', endpoint: '/api/admin/big-game/announce', body: { gameName: 'A Brief Case', announcement: 'A Brief Case is starting!', pauseTrading: true } },
      { id: 'start-phase-4', label: '⚡ Start Phase 4 (Power Hour)', endpoint: '/api/admin/trading-phase/start', body: { phase: 4 }, isPrimary: true }
    ]
  },
  {
    id: 'trading4',
    name: 'Power Hour',
    icon: '⚡',
    duration: 5,
    description: '5-min PURE TRADING - NO stock events, NO macro events - prices move only from trades',
    nextAction: 'Closing Bell',
    notes: '⚡ "Start Phase 4" = soft countdown (guide). When 5 min is up (or when ready), move to Closing Bell.',
    events: [
      { name: 'trading-phase-started', description: 'Phase 4 (power hour mode) - all events disabled', target: 'Web + ESP32' },
      { name: 'trading-resumed', description: '🔔 Resume bell (smaller than opening) + toast', target: 'Web + ESP32' },
      { name: 'trading-block-countdown-start', description: '5-min soft countdown (guide)', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'start-phase-4', label: '⚡ Start Phase 4 (soft countdown)', endpoint: '/api/admin/trading-phase/start', body: { phase: 4 }, isPrimary: true }
    ]
  },
  {
    id: 'closing-bell',
    name: 'Closing Bell',
    icon: '🔔',
    duration: 3,
    description: 'HARD countdown to close market - then Winners (or optional Liquidity Crisis first)',
    nextAction: 'Winners',
    notes: '🔔 HARD countdown (max 3 min) auto-closes market when done. After close: go to Winners OR run Liquidity Crisis first.',
    events: [
      { name: 'close-countdown-start', description: 'HARD countdown - auto-closes when done', target: 'Web + ESP32' },
      { name: 'market:closing-bell', description: 'Red collapse animation', target: 'Web + ESP32' },
      { name: 'market-bell (close)', description: 'Market CLOSED', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'closing-countdown', label: '🔔 Closing Bell Countdown (HARD)', endpoint: '/api/admin/show/close-countdown', body: { seconds: 60, autoCloseMarket: true }, hasCountdownInput: true, defaultSeconds: 60, isPrimary: true },
      { id: 'close-market', label: '🔔 Close Market NOW', endpoint: '/api/admin/market/toggle', body: { action: 'close' } }
    ]
  },
  {
    id: 'moneytunnel',
    name: 'Liquidity Crisis',
    icon: '🌀',
    duration: 15,
    description: 'OPTIONAL UNO-style finale for top players - skip to Winners if not running',
    nextAction: 'Winners',
    notes: '🎲 OPTIONAL after market closes! Either run this game OR skip straight to Winners.',
    events: [
      { name: 'big-game-announcement', description: 'Announce finale', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'announce-tunnel', label: '📢 Run Liquidity Crisis', endpoint: '/api/admin/big-game/announce', body: { gameName: 'Liquidity Crisis', announcement: 'Liquidity Crisis finale is starting!', pauseTrading: false }, isPrimary: true },
      { id: 'show-results', label: '⏭️ Skip to Winners', endpoint: '/api/admin/show/final-results', body: { topCount: 10 } }
    ]
  },
  {
    id: 'results',
    name: 'Winners',
    icon: '🏆',
    duration: 15,
    description: 'Winner announcements - 3rd, 2nd, 1st place',
    nextAction: 'Party Complete!',
    notes: '🏆 Announce winners! Click each place in order: 3rd → 2nd → 1st for dramatic reveals. Party ends after 1st place announcement!',
    events: [
      { name: 'final-results', description: 'Show leaderboard', target: 'Web + ESP32' },
      { name: 'winner-announcement (3rd)', description: 'Bronze reveal', target: 'Web + ESP32' },
      { name: 'winner-announcement (2nd)', description: 'Silver reveal', target: 'Web + ESP32' },
      { name: 'winner-announcement (1st)', description: 'Gold reveal', target: 'Web + ESP32' }
    ],
    actions: [
      { id: 'results', label: '📊 Show Final Results', endpoint: '/api/admin/show/final-results', body: { topCount: 10 } },
      { id: 'winner-3', label: '🥉 Announce 3rd', endpoint: '/api/admin/show/winner-announcement', body: { place: 3 } },
      { id: 'winner-2', label: '🥈 Announce 2nd', endpoint: '/api/admin/show/winner-announcement', body: { place: 2 } },
      { id: 'winner-1', label: '🥇 Announce 1st', endpoint: '/api/admin/show/winner-announcement', body: { place: 1 } }
    ]
  }
];

// Map party phase states to timeline phase IDs
const PHASE_MAPPING = {
  'not_started': 'not-started',
  'registration': 'registration-premarket',
  'premarket': 'registration-premarket',
  'orientation': 'orientation',
  'trading': null, // Will be determined by context
  'macro_game': null, // Will be determined by game name
  'ended': 'results'
};

const AdminTimeline = ({
  currentPhase,
  macroGameName,
  tradingBlockNumber = 1,
  completedGames = [],
  onPhaseConfig,
  durations: externalDurations,
  onDurationsChange
}) => {
  // Local state for durations (fetched from server)
  const [durations, setDurations] = useState(() => {
    // Default durations from TIMELINE_PHASES
    const defaults = {};
    TIMELINE_PHASES.forEach(p => {
      defaults[p.id] = p.duration;
    });
    return defaults;
  });

  const [showConfig, setShowConfig] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionResult, setActionResult] = useState(null);

  // State for countdown durations (per action that has countdown input)
  const [countdownDurations, setCountdownDurations] = useState(() => {
    const defaults = {};
    TIMELINE_PHASES.forEach(phase => {
      phase.actions?.forEach(action => {
        if (action.hasCountdownInput) {
          defaults[action.id] = action.defaultSeconds || 180;
        }
      });
    });
    return defaults;
  });

  // Use external durations if provided, otherwise use server durations
  const effectiveDurations = externalDurations || durations;

  // Fetch durations from server on mount
  useEffect(() => {
    const fetchDurations = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/timeline-durations`);
        const data = await response.json();
        if (data.success && data.durations) {
          setDurations(data.durations);
        }
      } catch (error) {
        console.log('Could not fetch timeline durations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDurations();

    // Listen for duration updates from other admins
    const handleDurationsUpdate = (data) => {
      if (data.durations) {
        setDurations(data.durations);
      }
    };

    socket.on('timeline-durations-update', handleDurationsUpdate);

    return () => {
      socket.off('timeline-durations-update', handleDurationsUpdate);
    };
  }, []);

  // Save durations to server when config is closed
  const saveDurationsToServer = useCallback(async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/timeline-durations`,
        { durations },
        { headers: { 'x-admin-token': getAdminToken() } }
      );
      console.log('Timeline durations saved to server');
    } catch (error) {
      console.error('Failed to save timeline durations:', error);
    } finally {
      setIsSaving(false);
    }
  }, [durations]);

  // Notify parent of changes
  useEffect(() => {
    if (onDurationsChange) {
      onDurationsChange(durations);
    }
  }, [durations, onDurationsChange]);

  // Reset durations to server defaults
  const resetDurationsToDefaults = useCallback(async () => {
    setIsSaving(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/timeline-durations/reset`,
        {},
        { headers: { 'x-admin-token': getAdminToken() } }
      );
      if (response.data.success && response.data.durations) {
        setDurations(response.data.durations);
      }
      console.log('Timeline durations reset to defaults');
    } catch (error) {
      console.error('Failed to reset timeline durations:', error);
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Determine current active phase in timeline
  const getActivePhaseId = () => {
    if (currentPhase === 'not_started') return 'not-started';
    if (currentPhase === 'registration' || currentPhase === 'premarket') return 'registration-premarket';
    if (currentPhase === 'orientation') return 'orientation';
    if (currentPhase === 'ended') return 'results';

    if (currentPhase === 'macro_game') {
      // Determine which macro game based on name
      const gameMap = {
        'Portfolio Warfare': 'battleship',
        'Portfolio': 'battleship',
        'Battleship': 'battleship',
        'Dead Cat Bounce': 'minigolf',
        'Gauntlet': 'minigolf',
        'Minigolf': 'minigolf',
        'No Deal': 'briefcase',
        'A Brief Case': 'briefcase',
        'Briefcase Blitz': 'briefcase',
        'Liquidity Crisis': 'moneytunnel',
        'Money Tunnel': 'moneytunnel'
      };
      return gameMap[macroGameName] || 'battleship';
    }

    // For trading phase, use the trading block number
    const tradingBlocks = ['trading1', 'trading2', 'trading3', 'trading4'];
    return tradingBlocks[Math.min(tradingBlockNumber - 1, 3)] || 'trading1';
  };

  const activePhaseId = getActivePhaseId();
  const activeIndex = TIMELINE_PHASES.findIndex(p => p.id === activePhaseId);

  // Calculate total time and elapsed time
  const calculateTotalTime = () => {
    return TIMELINE_PHASES.reduce((sum, p) => sum + (effectiveDurations[p.id] || p.duration), 0);
  };

  const calculateElapsedTime = () => {
    let elapsed = 0;
    for (let i = 0; i < activeIndex; i++) {
      elapsed += effectiveDurations[TIMELINE_PHASES[i].id] || TIMELINE_PHASES[i].duration;
    }
    return elapsed;
  };

  const handleDurationChange = (phaseId, newDuration) => {
    const updated = {
      ...durations,
      [phaseId]: parseInt(newDuration) || 0
    };
    setDurations(updated);
  };

  // Handle action button clicks
  const handleAction = async (action, phaseId) => {
    setActionLoading(action.id);
    setActionResult(null);
    try {
      // Build request body - use custom countdown duration if action has countdown input
      let requestBody = { ...action.body };
      if (action.hasCountdownInput && countdownDurations[action.id]) {
        requestBody.seconds = countdownDurations[action.id];
      }

      const response = await axios.post(
        `${API_BASE_URL}${action.endpoint}`,
        requestBody,
        { headers: { 'x-admin-token': getAdminToken() } }
      );
      setActionResult({ success: true, message: response.data.message || 'Action completed' });
      setTimeout(() => setActionResult(null), 3000);
    } catch (error) {
      console.error('Action failed:', error);
      setActionResult({ success: false, message: error.response?.data?.message || 'Action failed' });
      setTimeout(() => setActionResult(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle countdown duration change
  const handleCountdownDurationChange = (actionId, seconds) => {
    setCountdownDurations(prev => ({
      ...prev,
      [actionId]: parseInt(seconds) || 0
    }));
  };

  // Format seconds to mm:ss or m:ss
  const formatCountdownTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle phase expansion
  const togglePhaseExpand = (phaseId) => {
    setExpandedPhase(expandedPhase === phaseId ? null : phaseId);
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Calculate start times for each phase
  const getPhaseStartTime = (index) => {
    let startTime = 0;
    for (let i = 0; i < index; i++) {
      startTime += effectiveDurations[TIMELINE_PHASES[i].id] || TIMELINE_PHASES[i].duration;
    }
    return startTime;
  };

  return (
    <div className="admin-timeline">
      <div className="timeline-header">
        <h3>📅 Party Timeline</h3>
        <div className="timeline-meta">
          <span className="total-time">Total: {formatTime(calculateTotalTime())}</span>
          <span className="elapsed-time">Elapsed: ~{formatTime(calculateElapsedTime())}</span>
          <button
            className="config-toggle"
            onClick={() => {
              if (showConfig) {
                // Save to server when closing config
                saveDurationsToServer();
              }
              setShowConfig(!showConfig);
            }}
            disabled={isSaving}
          >
            {isSaving ? '💾 Saving...' : showConfig ? '✓ Save & Close' : '⚙️ Configure'}
          </button>
          {showConfig && (
            <button
              className="reset-defaults-btn"
              onClick={resetDurationsToDefaults}
              disabled={isSaving}
              title="Reset all durations to default values"
            >
              🔄 Reset Defaults
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="timeline-progress">
        <div
          className="progress-fill"
          style={{ width: `${(activeIndex / (TIMELINE_PHASES.length - 1)) * 100}%` }}
        />
        <div className="progress-markers">
          {TIMELINE_PHASES.map((phase, index) => (
            <div
              key={phase.id}
              className={`progress-marker ${index <= activeIndex ? 'completed' : ''} ${index === activeIndex ? 'active' : ''}`}
              style={{ left: `${(index / (TIMELINE_PHASES.length - 1)) * 100}%` }}
              title={phase.name}
            />
          ))}
        </div>
      </div>

      {/* Timeline Phases */}
      <div className="timeline-phases">
        {TIMELINE_PHASES.map((phase, index) => {
          const isActive = phase.id === activePhaseId;
          const isCompleted = index < activeIndex;
          const isPending = index > activeIndex;
          const duration = effectiveDurations[phase.id] || phase.duration;
          const startTime = getPhaseStartTime(index);

          const isExpanded = expandedPhase === phase.id;

          return (
            <div
              key={phase.id}
              className={`timeline-phase ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isPending ? 'pending' : ''} ${isExpanded ? 'expanded' : ''}`}
            >
              <div className="phase-connector">
                <div className="connector-line" />
                <div className="connector-dot">
                  {isCompleted ? '✓' : phase.icon}
                </div>
              </div>

              <div className="phase-content">
                <div
                  className="phase-header"
                  onClick={() => togglePhaseExpand(phase.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="phase-name">{phase.name}</span>
                  {showConfig ? (
                    <div className="phase-duration-edit" onClick={e => e.stopPropagation()}>
                      <input
                        type="number"
                        min="1"
                        max="120"
                        value={duration}
                        onChange={(e) => handleDurationChange(phase.id, e.target.value)}
                        className="duration-input"
                      />
                      <span className="duration-unit">min</span>
                    </div>
                  ) : (
                    <span className="phase-duration">{formatTime(duration)}</span>
                  )}
                  <span className="phase-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                </div>
                <div className="phase-description">{phase.description}</div>
                {phase.notes && (
                  <div className={`phase-notes ${isActive ? 'active' : ''}`}>
                    {phase.notes}
                  </div>
                )}
                {isActive && (
                  <div className="phase-next-action">
                    Next: <strong>{phase.nextAction}</strong>
                  </div>
                )}
                {!showConfig && (
                  <div className="phase-time-marker">
                    +{formatTime(startTime)}
                  </div>
                )}

                {/* Expanded Events & Actions */}
                {isExpanded && (
                  <div className="phase-details">
                    {/* Events List */}
                    <div className="phase-events">
                      <div className="events-header">
                        <span className="events-title">Events that fire:</span>
                        <span className="events-count">{phase.events?.length || 0}</span>
                      </div>
                      <div className="events-list">
                        {phase.events?.map((event, idx) => (
                          <div key={idx} className="event-item">
                            <span className="event-name">{event.name}</span>
                            <span className="event-description">{event.description}</span>
                            <span className={`event-target ${event.target.includes('ESP32') ? 'has-esp32' : ''}`}>
                              {event.target}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {phase.actions && phase.actions.length > 0 && (
                      <div className="phase-actions">
                        <div className="actions-header">Actions:</div>
                        <div className="actions-list">
                          {phase.actions.map((action) => (
                            <div key={action.id} className="action-item">
                              {action.hasCountdownInput && (
                                <div className="countdown-input-group">
                                  <input
                                    type="number"
                                    min="10"
                                    max="600"
                                    value={countdownDurations[action.id] || action.defaultSeconds}
                                    onChange={(e) => handleCountdownDurationChange(action.id, e.target.value)}
                                    className="countdown-seconds-input"
                                    title="Countdown duration in seconds"
                                  />
                                  <span className="countdown-display">
                                    {formatCountdownTime(countdownDurations[action.id] || action.defaultSeconds)}
                                  </span>
                                </div>
                              )}
                              <button
                                className={`action-button ${actionLoading === action.id ? 'loading' : ''}`}
                                onClick={() => handleAction(action, phase.id)}
                                disabled={actionLoading === action.id}
                              >
                                {actionLoading === action.id ? '...' : action.label}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Result Feedback */}
                    {actionResult && (
                      <div className={`action-result ${actionResult.success ? 'success' : 'error'}`}>
                        {actionResult.success ? '✓' : '✗'} {actionResult.message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Current Phase Summary */}
      <div className="timeline-current-summary">
        <div className="current-phase-indicator">
          <span className="current-icon">{TIMELINE_PHASES[activeIndex]?.icon}</span>
          <div className="current-info">
            <span className="current-label">Currently:</span>
            <span className="current-name">{TIMELINE_PHASES[activeIndex]?.name}</span>
          </div>
        </div>
        <div className="next-phase-preview">
          {activeIndex < TIMELINE_PHASES.length - 1 && (
            <>
              <span className="next-label">Next up:</span>
              <span className="next-icon">{TIMELINE_PHASES[activeIndex + 1]?.icon}</span>
              <span className="next-name">{TIMELINE_PHASES[activeIndex + 1]?.name}</span>
              <span className="next-duration">({formatTime(effectiveDurations[TIMELINE_PHASES[activeIndex + 1]?.id] || TIMELINE_PHASES[activeIndex + 1]?.duration)})</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTimeline;
